/**
 * Data Layer Management
 *
 * This module handles GeoJSON data layers integration with the map:
 * - Health Sites data layer
 * - Isochrone API integration
 */

import mapboxgl from 'mapbox-gl';
import { getMapManager } from '../components/Map/MapManager.js';

// Track if data layers are enabled
let dataLayerEnabled = false;
let isochroneLayerEnabled = false;

// Layer and source IDs
const sourceId = 'health-sites-source';
const pointsLayerId = 'health-sites-points-layer';
const isochroneSourceId = 'isochrone-source';
const isochroneLayerId = 'isochrone-layer';

// Default isochrone settings (as specified - walking 15m radius)
const isochroneSettings = {
    profile: 'mapbox/walking',
    minutes: [15],
    opacity: 0.1
};

// Store loaded health sites data
let healthSitesData = null;

/**
 * Add Health Sites GeoJSON data layer to the map
 * If already loaded, just makes the layer visible
 */
export function addHealthSitesLayer() {
    console.log('[HealthSitesLayer] Adding health sites data layer');
    const mapManager = getMapManager();
    if (!mapManager || !mapManager.getMap()) {
        console.error('[HealthSitesLayer] Map not initialized');
        return;
    }

    const map = mapManager.getMap();

    try {
        // Check if source already exists
        if (!map.getSource(sourceId)) {
            // Add source with GeoJSON data
            map.addSource(sourceId, {
                type: 'geojson',
                data: '/data/healthsites.geojson'
            });
            
            // Add point layer for health sites
            map.addLayer({
                id: pointsLayerId,
                type: 'circle',
                source: sourceId,
                layout: {
                    'visibility': 'visible'
                },
                paint: {
                    'circle-radius': 2,

                    'circle-color': [
                        'match',
                        ['get', 'amenity'],
                        'hospital', '#875CFF', // Red for hospitals
                        'clinic', '#634EFF',   // Blue for clinics
                        'doctors', '#57D3EF',  // Purple for doctors
                        'pharmacy', '#3BECB4', // Green for pharmacies
                        'dentist', '#CFFF76',  // Orange for dentists
                        '#FFB754'  // Default gray for other health facilities
                    ],
                    'circle-stroke-width': 1,
                    'circle-stroke-color': '#000000',
                    'circle-opacity': 1
                },
                // Filter to only show Point geometries (all should be points in this dataset)
                filter: ['==', ['geometry-type'], 'Point']
            });
            
            console.log('[HealthSitesLayer] Added GeoJSON source and layer');
            
            // Load the health sites data for generating isochrones later
            fetchHealthSitesData();
        } else {
            // If layer already exists, just update visibility
            map.setLayoutProperty(pointsLayerId, 'visibility', 'visible');
            console.log('[HealthSitesLayer] Set existing layer to visible');
        }
        
        dataLayerEnabled = true;
        
        // Set up popup for health sites
        setupHealthSitePopups(map);
        
    } catch (error) {
        console.error('[HealthSitesLayer] Error adding data layer:', error);
    }
}

/**
 * Fetch the health sites GeoJSON data for later use
 */
async function fetchHealthSitesData() {
    try {
        const response = await fetch('/data/healthsites.geojson');
        if (!response.ok) {
            throw new Error(`Failed to fetch health sites data: ${response.status}`);
        }
        healthSitesData = await response.json();
        console.log('[HealthSitesLayer] Loaded health sites data with', 
            healthSitesData.features ? healthSitesData.features.length : 0, 'features');
    } catch (error) {
        console.error('[HealthSitesLayer] Error fetching health sites data:', error);
    }
}

/**
 * Set up click handlers for health sites
 * @param {Object} map - Mapbox map instance
 */
function setupHealthSitePopups(map) {
    // Remove any existing handler first to prevent duplicates
    map.off('click', pointsLayerId);
    
    console.log('[HealthSitesLayer] Setting up health site click handlers');
    
    // Add click handler for health site points
    map.on('click', pointsLayerId, (e) => {
        console.log('[HealthSitesLayer] Health site clicked');
        
        // Get the clicked feature
        const features = map.queryRenderedFeatures(e.point, { layers: [pointsLayerId] });
        if (!features.length) {
            console.log('[HealthSitesLayer] No features found at click point');
            return;
        }
        
        const feature = features[0];
        const props = feature.properties;
        
        // Get facility type for color selection
        const facilityType = props.amenity || props.healthcare || 'health';
        
        // Create popup content with health site info
        const html = `
            <div class="data-popup">
                <h3>${props.name || 'Unnamed Health Facility'}</h3>
                <p>Type: ${props.amenity || props.healthcare || 'Health facility'}</p>
                ${props.speciality ? `<p>Speciality: ${props.speciality}</p>` : ''}
                ${props.operational_status ? `<p>Status: ${props.operational_status}</p>` : ''}
                ${props.emergency === 'yes' ? `<p><strong>Emergency services available</strong></p>` : ''}
            </div>
        `;
        
        // Create and display popup
        const popup = new mapboxgl.Popup()
            .setLngLat(e.lngLat)
            .setHTML(html)
            .addTo(map);
        
        // Immediately create isochrone for this health facility
        const coordinates = [e.lngLat.lng, e.lngLat.lat];
        createSingleIsochrone(coordinates, facilityType);
    });
    
    // Change cursor on hover
    map.on('mouseenter', pointsLayerId, () => {
        map.getCanvas().style.cursor = 'pointer';
    });
    
    map.on('mouseleave', pointsLayerId, () => {
        map.getCanvas().style.cursor = '';
    });
}

/**
 * Remove (hide) health sites data layer from the map
 * Actually just hides the layer to preserve the data
 */
export function removeHealthSitesLayer() {
    console.log('[HealthSitesLayer] Hiding health sites data layer');
    const mapManager = getMapManager();
    if (!mapManager || !mapManager.getMap()) {
        console.error('[HealthSitesLayer] Map not initialized');
        return;
    }

    const map = mapManager.getMap();

    try {
        // Hide the layer
        if (map.getLayer(pointsLayerId)) {
            map.setLayoutProperty(pointsLayerId, 'visibility', 'none');
        }
        
        dataLayerEnabled = false;
        console.log('[HealthSitesLayer] Successfully hidden health sites data layer');
    } catch (error) {
        console.error('[HealthSitesLayer] Error hiding health sites data layer:', error);
    }
}

/**
 * Completely remove health sites data layer and source from the map
 * Used before style changes
 */
export function completelyRemoveHealthSitesLayer() {
    console.log('[HealthSitesLayer] Completely removing health sites data layer');
    const mapManager = getMapManager();
    if (!mapManager || !mapManager.getMap()) {
        console.error('[HealthSitesLayer] Map not initialized');
        return;
    }

    const map = mapManager.getMap();

    try {
        // Remove layer first
        if (map.getLayer(pointsLayerId)) {
            map.removeLayer(pointsLayerId);
        }
        
        // Then remove source
        if (map.getSource(sourceId)) {
            map.removeSource(sourceId);
        }
        
        dataLayerEnabled = false;
        console.log('[HealthSitesLayer] Successfully completely removed health sites data layer');
    } catch (error) {
        console.error('[HealthSitesLayer] Error completely removing health sites data layer:', error);
    }
}

/**
 * Toggle health sites data layer visibility
 */
export function toggleHealthSitesLayer() {
    if (dataLayerEnabled) {
        removeHealthSitesLayer();
        return false;
    } else {
        addHealthSitesLayer();
        return true;
    }
}

// Initialize click handler for data button
document.addEventListener('DOMContentLoaded', () => {
    const dataButton = document.querySelector('.data-button');
    if (!dataButton) {
        console.warn('[HealthSitesLayer] Data button not found');
        return;
    }
    
    // Add a tooltip to explain functionality
    dataButton.title = 'Click to toggle health sites data layer with isochrones';
    
    // Click handler
    dataButton.addEventListener('click', () => {
        console.log(`[HealthSitesLayer] Data button clicked, current state: ${dataLayerEnabled}`);
        
        const enabled = toggleHealthSitesLayer();
        if (enabled) {
            dataButton.classList.add('active');
        } else {
            dataButton.classList.remove('active');
            
            // Remove isochrone when disabling the layer
            if (isochroneLayerEnabled) {
                console.log('[HealthSitesLayer] Removing isochrone layer with health sites layer');
                removeIsochroneLayer();
            }
        }
    });
});

/**
 * Create a single isochrone for a clicked health facility
 * @param {Array} coordinates [lng, lat] location for the isochrone
 * @param {String} facilityType Type of health facility (hospital, clinic, etc.)
 */
async function createSingleIsochrone(coordinates, facilityType) {
    console.log('[Isochrone] Creating single isochrone at', coordinates, 'for facility type', facilityType);
    
    try {
        // Get appropriate color based on facility type
        const color = getHealthFacilityColor(facilityType);
        
        // Fetch isochrone data from API
        const geojson = await fetchIsochroneData(
            coordinates,
            isochroneSettings.profile,
            isochroneSettings.minutes,
            color
        );
        
        // Apply custom styling to features
        if (geojson.features && geojson.features.length > 0) {
            geojson.features.forEach(feature => {
                if (feature.properties) {
                    feature.properties['fill-opacity'] = isochroneSettings.opacity;
                    feature.properties['fillOpacity'] = isochroneSettings.opacity;
                    feature.properties['opacity'] = isochroneSettings.opacity;
                    feature.properties['stroke-width'] = 0;
                    feature.properties['strokeWidth'] = 0;
                }
            });
        }
        
        // Add isochrone to map
        addIsochrones(geojson);
        
    } catch (error) {
        console.error('[Isochrone] Error creating single isochrone:', error);
    }
}

/**
 * Generate isochrones for all health sites
 * Note: This function is kept for reference but no longer used
 */
async function generateIsochronesForAllHealthSites() {
    if (!healthSitesData || !healthSitesData.features || healthSitesData.features.length === 0) {
        console.warn('[Isochrone] No health sites data available for generating isochrones');
        await fetchHealthSitesData();
        
        if (!healthSitesData || !healthSitesData.features || healthSitesData.features.length === 0) {
            console.error('[Isochrone] Failed to fetch health sites data for isochrones');
            return;
        }
    }
    
    console.log('[Isochrone] Generating combined isochrones for all health sites');
    
    // Extract all point coordinates and facility types
    const points = healthSitesData.features
        .filter(feature => feature.geometry && feature.geometry.type === 'Point')
        .map(feature => {
            return {
                coordinates: feature.geometry.coordinates,
                facilityType: feature.properties.amenity || feature.properties.healthcare || 'health'
            };
        });
    
    if (points.length === 0) {
        console.warn('[Isochrone] No valid points found in health sites data');
        return;
    }
    
    // Create a combined GeoJSON with all isochrones
    const combinedFeatures = [];
    
    // Process in batches to avoid overwhelming the API
    const batchSize = 20;
    const totalPoints = points.length;
    
    console.log(`[Isochrone] Processing ${totalPoints} health sites in batches of ${batchSize}`);
    
    // Process ALL points in the dataset
    for (let i = 0; i < totalPoints; i += batchSize) {
        const batch = points.slice(i, i + batchSize);
        
        // Process batch in parallel
        const batchPromises = batch.map(async point => {
            try {
                const geojson = await fetchIsochroneData(
                    point.coordinates,
                    isochroneSettings.profile,
                    isochroneSettings.minutes,
                    getHealthFacilityColor(point.facilityType)
                );
                
                // Apply opacity to features
                if (geojson.features && geojson.features.length > 0) {
                    geojson.features.forEach(feature => {
                        if (feature.properties) {
                            feature.properties['fill-opacity'] = isochroneSettings.opacity;
                            feature.properties['fillOpacity'] = isochroneSettings.opacity;
                            feature.properties['opacity'] = isochroneSettings.opacity;
                            feature.properties['stroke-width'] = 0;
                            feature.properties['strokeWidth'] = 0;
                        }
                    });
                    
                    // Add features to combined collection
                    combinedFeatures.push(...geojson.features);
                }
            } catch (error) {
                console.error(`[Isochrone] Error fetching isochrone for point ${point.coordinates}:`, error);
            }
        });
        
        await Promise.all(batchPromises);
        console.log(`[Isochrone] Processed batch ${i / batchSize + 1}`);
    }
    
    // Create a combined GeoJSON
    const combinedGeoJSON = {
        type: 'FeatureCollection',
        features: combinedFeatures
    };
    
    // Add the combined isochrones to the map
    addIsochrones(combinedGeoJSON);
}

/**
 * Add isochrones to the map from a GeoJSON object
 * @param {Object} geojsonData The GeoJSON data containing isochrone polygons
 */
function addIsochrones(geojsonData) {
    console.log('[Isochrone] Adding isochrones layer with', 
        geojsonData.features ? geojsonData.features.length : 0, 'features');
    
    const mapManager = getMapManager();
    if (!mapManager || !mapManager.getMap()) {
        console.error('[Isochrone] Map not initialized');
        return;
    }

    const map = mapManager.getMap();
    
    try {
        // Remove any existing isochrone layers first
        removeIsochroneLayer();
        
        // Add source and layer to the map
        map.addSource(isochroneSourceId, {
            type: 'geojson',
            data: geojsonData
        });
        
        map.addLayer({
            id: isochroneLayerId,
            type: 'fill',
            source: isochroneSourceId,
            layout: {
                'visibility': 'visible'
            },
            paint: {
                'fill-color': ['get', 'fill'],
                'fill-opacity': ['get', 'fill-opacity'],
                'fill-outline-color': 'transparent' // No stroke
            }
        });
        
        isochroneLayerEnabled = true;
        console.log('[Isochrone] Combined isochrones layer added successfully');
    } catch (error) {
        console.error('[Isochrone] Error adding isochrones layer:', error);
        // Clean up any partial additions
        completelyRemoveIsochroneLayer();
    }
}

/**
 * Fetch isochrone data from Mapbox API for a given location
 * @param {Array} coordinates [lng, lat] location to generate isochrone
 * @param {String} profile Transportation profile (walking, cycling, driving)
 * @param {Array} minutes Array of minute contours
 * @param {String} color Color for the isochrone (hex without #)
 * @returns {Promise} GeoJSON data for isochrones
 */
async function fetchIsochroneData(coordinates, profile, minutes, color) {
    // Build API URL with parameters
    const minutesString = minutes.join(',');
    
    // Get color for isochrone based on the health facility type
    const colors = minutes.map(() => color || '875CFF').join(',');
    
    const url = `https://api.mapbox.com/isochrone/v1/${profile}/${coordinates[0]},${coordinates[1]}?contours_minutes=${minutesString}&contours_colors=${colors}&polygons=true&access_token=${mapboxgl.accessToken}`;
    
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`API Error: ${response.status} - ${await response.text()}`);
        }
        return await response.json();
    } catch (error) {
        console.error('[Isochrone] Error fetching isochrone data:', error);
        throw error;
    }
}

/**
 * Get the appropriate color for a health facility based on its type
 * @param {String} facilityType The type of health facility
 * @returns {String} Hex color code (without #)
 */
function getHealthFacilityColor(facilityType) {
    switch(facilityType) {
        case 'hospital': return '875CFF';
        case 'clinic': return '634EFF';
        case 'doctors': return '57D3EF';
        case 'pharmacy': return '3BECB4';
        case 'dentist': return 'CFFF76';
        default: return 'FFB754'; // Default for other health facilities
    }
}

/**
 * Remove (hide) isochrone layer from the map
 */
export function removeIsochroneLayer() {
    console.log('[Isochrone] Removing isochrone layer');
    
    const mapManager = getMapManager();
    if (!mapManager || !mapManager.getMap()) {
        console.error('[Isochrone] Map not initialized');
        return;
    }

    const map = mapManager.getMap();
    
    try {
        // Remove layer and source if they exist
        if (map.getLayer(isochroneLayerId)) {
            map.removeLayer(isochroneLayerId);
        }
        
        if (map.getSource(isochroneSourceId)) {
            map.removeSource(isochroneSourceId);
        }
        
        isochroneLayerEnabled = false;
        console.log('[Isochrone] Isochrone layer removed successfully');
    } catch (error) {
        console.error('[Isochrone] Error removing isochrone layer:', error);
    }
}

/**
 * Completely remove isochrone layer and source from the map
 * Used before style changes
 */
export function completelyRemoveIsochroneLayer() {
    // Same implementation as removeIsochroneLayer for now
    removeIsochroneLayer();
}

/**
 * Show a notification message on the map
 * @param {String} message The message to display
 * @param {String} type The type of message (default, success, error)
 */
function showNotification(message, type = 'default') {
    console.log(`[Notification] ${message}`);
    
    // Check if we're in the browser
    if (typeof document === 'undefined') return;
    
    // Create or get the notification container
    let container = document.getElementById('map-notifications');
    if (!container) {
        container = document.createElement('div');
        container.id = 'map-notifications';
        container.style.position = 'absolute';
        container.style.top = '20px';
        container.style.left = '50%';
        container.style.transform = 'translateX(-50%)';
        container.style.zIndex = '1000';
        container.style.pointerEvents = 'none';
        
        // Find the map container and add the notifications container to it
        const mapContainer = document.querySelector('.mapboxgl-map');
        if (mapContainer) {
            mapContainer.appendChild(container);
        } else {
            document.body.appendChild(container);
        }
    }
    
    // Create the notification element
    const notification = document.createElement('div');
    notification.className = `map-notification ${type}`;
    notification.style.padding = '10px 15px';
    notification.style.marginBottom = '10px';
    notification.style.borderRadius = '4px';
    notification.style.backgroundColor = type === 'error' ? 'rgba(255, 0, 0, 0.8)' :
                                       type === 'success' ? 'rgba(0, 128, 0, 0.8)' :
                                       'rgba(0, 0, 0, 0.8)';
    notification.style.color = 'white';
    notification.style.fontSize = '14px';
    notification.style.fontWeight = 'bold';
    notification.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.3)';
    notification.style.transition = 'opacity 0.5s ease-in-out';
    notification.style.textAlign = 'center';
    notification.innerText = message;
    
    // Add the notification to the container
    container.appendChild(notification);
    
    // Automatically remove the notification after a timeout
    setTimeout(() => {
        notification.style.opacity = '0';
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 500);
    }, 5000);
}

// Expose functions to window for MapManager
if (typeof window !== 'undefined') {
    window.addHealthSitesLayer = addHealthSitesLayer;
    window.removeHealthSitesLayer = removeHealthSitesLayer;
    window.completelyRemoveHealthSitesLayer = completelyRemoveHealthSitesLayer;
    
    // Expose isochrone functions
    window.removeIsochroneLayer = removeIsochroneLayer;
    window.completelyRemoveIsochroneLayer = completelyRemoveIsochroneLayer;
}